import { unwrapResult } from "@reduxjs/toolkit"
import { Localized } from "@fluent/react"
import { Box, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useCallback, useEffect, useState } from "react"
import { ErrorContainer } from "../../../../app/ErrorContainer"
import { useAppDispatch } from "../../../../app/hooks"
import { LoadingContainer } from "../../../../app/LoadingContainer"
import BadgeAvatar from "../../../mockups/BadgeAvatar"
import { loadDiscussionMembers, DiscussionMemberDto, RELATED_OBJECT_TYPE } from "../discussionsSlice"

export const ImportedDocumentDiscussionMembers = ({ relatedObjectId }: { relatedObjectId: string }) => {
    const dispatch = useAppDispatch()

    const [members, setMembers] = useState<DiscussionMemberDto[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isError, setIsError] = useState(false)
    const [error, setError] = useState<unknown>(null)

    const load = useCallback(() => {
        setIsLoading(true)
        setIsError(false)
        setError(null)

        return dispatch(loadDiscussionMembers({
            relatedObjectType: RELATED_OBJECT_TYPE,
            relatedObjectId,
        }))
            .then(unwrapResult)
            .then(setMembers)
            .catch((e) => {
                console.error(e)
                setIsError(true)
                setError(e)
            })
            .finally(() => setIsLoading(false))
    }, [dispatch, relatedObjectId])

    useEffect(() => {
        load()
    }, [load])

    if (isLoading) {
        return <LoadingContainer />
    }

    if (isError) {
        return <ErrorContainer error={error} />
    }

    return (
        <Paper elevation={1} sx={{ mt: 1, mb: 1, overflow: "hidden" }}>
            <Box
                sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <ForumOutlinedIcon fontSize="medium" sx={{ mr: 1, color: "text.secondary" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        <Localized id="discussion-participants">_Osoby biorace udzial w dyskusji</Localized>
                    </Typography>
                </Box>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 72, fontWeight: 600 }}>#</TableCell>
                            <TableCell sx={{ minWidth: 220, fontWeight: 600 }}>
                                <Localized id="user">
                                    <span>_Użytkownik</span>
                                </Localized>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {members.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <Typography variant="body2" sx={{ py: 2, fontStyle: "italic" }} color="text.secondary">
                                        <Localized id="discussion-empty">
                                            _Brak wiadomości. Dyskusja nierozpoczęta.
                                        </Localized>
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            members.map((member, index) => (
                                <DiscussionMemberRow key={member.userId} member={member} index={index} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    )
}

const DiscussionMemberRow = ({ member, index }: { member: DiscussionMemberDto, index: number }) => {
    return (
        <TableRow hover>
            <TableCell sx={{ width: 72, verticalAlign: "top" }}>{index + 1}</TableCell>
            <TableCell sx={{ verticalAlign: "top" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <BadgeAvatar
                        firstName={member.fullName || "?"}
                        surname=""
                        userId={member.userId}
                    />

                    <Box display="flex" flexDirection="column" minWidth={0}>
                        <Typography
                            variant="body2"
                            fontWeight={500}
                            noWrap
                        >
                            {member.fullName || "-"}
                        </Typography>

                        {member.position && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ lineHeight: 1.3 }}
                            >
                                {member.position}
                            </Typography>
                        )}

                        {member.email && (
                            <Link
                                href={`mailto:${member.email}`}
                                underline="none"
                                title={member.email}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    minWidth: 0,
                                    mt: 0.2,
                                    color: 'text.disabled',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                <EmailOutlinedIcon
                                    sx={{
                                        fontSize: 13,
                                        color: 'inherit',
                                        flexShrink: 0,
                                    }}
                                />

                                <Typography
                                    variant="caption"
                                    noWrap
                                    sx={{
                                        lineHeight: 1.3,
                                        fontSize: '0.72rem',
                                        color: 'inherit',
                                    }}
                                >
                                    {member.email}
                                </Typography>
                            </Link>
                        )}
                    </Box>
                </Box>
            </TableCell>
        </TableRow>
    )
}